import { useEffect, useMemo, useState } from "react";
import { Select, Spin } from "antd";
import type { SelectProps } from "antd";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../helpers/api";
import { fmtItem } from "../helpers/format";
import type { Item, Paginated } from "../api-models";

/** How long typing has to settle before a request goes out. */
const DEBOUNCE_MS = 150;
/** Rows a search returns - one dropdown's worth, not the master. */
const PAGE_SIZE = 20;
const STALE_MS = 5 * 60 * 1000;

export type ItemSelectValue = number | string;

type ExtraOption = { value: string; label: string };

type ItemSelectProps = Omit<SelectProps<ItemSelectValue>, "options" | "value" | "onChange" | "onSearch" | "filterOption"> & {
  value?: ItemSelectValue | null;
  /** The chosen item's id (or one of extraOptions' values), and the Item record when it is an item. */
  onChange?: (value: ItemSelectValue | undefined, item?: Item) => void;
  /**
   * Items the caller already holds - a document's lines arrive with their item attached -
   * so the picker shows them without asking the server again. Anything selected that is
   * neither here nor in the current search is fetched by id.
   */
  knownItems?: Item[];
  /** Extra /items filters, e.g. { type: "TOOL" }. Inactive items are never offered. */
  params?: Record<string, string | number | boolean>;
  /** Non-item choices appended after the items, e.g. a BOM row's NON_STOCK / REJECTED. */
  extraOptions?: ExtraOption[];
};

/**
 * The item picker. The master runs to thousands of records, so it is searched on the
 * server rather than loaded: the dropdown opens on the first page, narrows as the user
 * types, and always carries whatever is already selected - even when that item is nowhere
 * in the page shown - so a picked row never renders as a bare id. Drop-in for the plain
 * Select, inside a Form.Item or controlled by the page's own state.
 */
export function ItemSelect({ value, onChange, knownItems = [], params, extraOptions = [], ...rest }: ItemSelectProps) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [term, setTerm] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setTerm(typed.trim()), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [typed]);

  // Results live in this picker's own state and are fetched imperatively, not through a
  // useQuery subscription: a BOM page carries a picker per row, and a shared subscription
  // would re-render every one of them each time the open one got a page back. The cache
  // is still shared, so the second picker opened on the page shows the first page at once.
  const queryClient = useQueryClient();
  const [results, setResults] = useState<Item[]>([]);
  const [fetching, setFetching] = useState(false);
  useEffect(() => {
    if (!open && term === "") return;
    let live = true;
    setFetching(true);
    queryClient
      .fetchQuery({
        queryKey: ["items", "picker", params ?? {}, term],
        queryFn: async () =>
          (await api.get<Paginated<Item>>("/items", { params: { ...params, active: true, search: term || undefined, pageSize: PAGE_SIZE } }))
            .data.data,
        staleTime: STALE_MS,
      })
      .then((rows) => live && setResults(rows))
      .catch(() => live && setResults([]))
      .finally(() => live && setFetching(false));
    return () => {
      live = false;
    };
    // params is an inline object literal at most call sites; keying on its JSON keeps it stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, term, JSON.stringify(params ?? {})]);

  const known = useMemo(() => new Map(knownItems.map((it) => [it.id, it])), [knownItems]);
  const selectedId = typeof value === "number" ? value : undefined;
  const needsLookup = selectedId != null && !known.has(selectedId) && !results.some((it) => it.id === selectedId);
  const lookup = useQuery({
    queryKey: ["items", "byId", selectedId],
    queryFn: async () => (await api.get<Paginated<Item>>("/items", { params: { ids: selectedId, pageSize: 1 } })).data.data[0],
    enabled: needsLookup,
    staleTime: STALE_MS,
  });

  const options = useMemo(() => {
    const seen = new Set<number>();
    const out: { value: ItemSelectValue; label: string; item?: Item }[] = [];
    const add = (it?: Item) => {
      if (!it || seen.has(it.id)) return;
      seen.add(it.id);
      out.push({ value: it.id, label: fmtItem(it), item: it });
    };
    // The selection first, so it is present whatever the search returned.
    if (selectedId != null) add(known.get(selectedId) ?? lookup.data);
    results.forEach(add);
    return [...out, ...extraOptions];
  }, [selectedId, known, lookup.data, results, extraOptions]);

  return (
    <Select<ItemSelectValue>
      showSearch
      placeholder="Search by code or name"
      {...rest}
      value={value ?? undefined}
      options={options}
      filterOption={false}
      searchValue={typed}
      onSearch={setTyped}
      open={open}
      onOpenChange={(visible) => {
        setOpen(visible);
        if (!visible) setTyped("");
      }}
      onChange={(v, opt) => onChange?.(v, (Array.isArray(opt) ? opt[0] : opt)?.item as Item | undefined)}
      loading={fetching}
      notFoundContent={fetching ? <Spin size="small" /> : term ? "No items match." : "Type to search the Item Master."}
    />
  );
}
